// src/pages/nouh.jsx
import React, { useEffect, useState } from "react";
import { FaFacebookF, FaInstagram, FaTwitter, FaWhatsapp, FaYoutube } from "react-icons/fa";
import { getAllMasjidData } from "../services/dynamicApiService";
import { getHijriDate } from "../services/hijriService";
import { getMasjidServices } from "../services/moduleService";
import { getPrayerTimes } from "../services/masjidService";
import { DEFAULT_MASJID_ID } from "../config/apiConfig";

// pages
import HomePage from "./homepage";
import ServicesPage from "./Servicespage";
import EventsPage from "./eventpage";
import ContactPage from "./contactpage";

// assets used in header/footer
import logo from "../assets/nouh-logo.jpg";

const MASJID_ID = DEFAULT_MASJID_ID;

/* ------------ helpers ------------ */
const normalizeEvent = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const startDate =
    raw.startDate ?? raw.start_date ?? raw.date ?? raw.start ?? raw.startOn ?? null;
  const endDate =
    raw.endDate ?? raw.end_date ?? raw.until ?? raw.repeatEndDate ?? raw.end ?? null;

  return {
    ...raw,
    name: raw.name ?? raw.title ?? "Event",
    title: raw.title ?? raw.name ?? "Event",
    startDate: startDate || null,
    endDate: endDate || startDate || null,
    time: raw.time ?? raw.startTime ?? raw.startsAt ?? null,
    location: raw.location ?? raw.venue ?? raw.place ?? null,
    image: raw.image,
  };
};

// Tiny preloader just for the app shell
const Preloader = ({ isLoading }) => {
  if (!isLoading) return null;
  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col items-center justify-center">
      <div className="mb-5 h-16 w-16 bg-[#005333] rounded-full flex items-center justify-center">
        <span className="text-white font-bold text-lg">NOUH</span>
      </div>
      <div className="h-1 w-20 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-[#005333] animate-pulse"></div>
      </div>
    </div>
  );
};




const Header = ({ currentPage, setCurrentPage, isMenuOpen, setIsMenuOpen }) => {
  const navItems = [
    { id: "home", label: "Home" },
    { id: "about", label: "About" },
    { id: "services", label: "Services" },
    { id: "events", label: "Events" },
    { id: "announcements", label: "Announcements" },
    { id: "contact", label: "Contact" },
  ];

  const scrollToSection = (sectionId) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.getElementById(sectionId)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });
  };

  return (
    <header className="header bg-white shadow-sm sticky top-0 z-40">
      <nav className="navbar">
        <div className="w-full px-4 py-3">
          <div className="flex items-center justify-between">
            <button onClick={() => setCurrentPage("home")} className="navbar-logo flex items-center">
              <img src={logo} alt="NOUH Islamic Centre of Canada" className="h-14 md:h-16 w-auto max-w-[72px] mr-3 object-contain" />
              <div className="text-left">
                <h2 className="text-lg font-bold">NOUH</h2>
                <p className="text-sm font-bold text-black">Islamic Centre of Canada</p>
              </div>
            </button>

            <div className="flex items-center gap-3">
              <a
                href={process.env.REACT_APP_DONATION_URL || "https://donorchoice.ca/nouh"}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:inline-flex bg-[#0f9e68] text-white px-7 py-3.5 rounded-lg text-lg font-bold transition-colors hover:bg-[#005333]"
              >
                Donate Now
              </a>

              <button className="md:hidden border-0 p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                <span className="text-[#0f9e68] font-bold text-xl">&#9776;</span>
              </button>
            </div>
          </div>

          <div className="md:hidden mt-3 pt-3 border-t border-[#d3ebd6]">
            <a
              href={process.env.REACT_APP_DONATION_URL || "https://donorchoice.ca/nouh"}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-[#0f9e68] text-white text-center px-7 py-3.5 rounded-lg text-lg font-bold transition-colors hover:bg-[#005333]"
            >
              Donate Now
            </a>
          </div>

          <div
            className={`${isMenuOpen ? "block" : "hidden"} md:block absolute md:static top-full left-0 right-0 bg-white md:bg-[#005333] shadow-md md:shadow-none p-4 md:-mx-4 md:px-4 md:py-3 md:mt-3 md:border-[#d3ebd6]`}
          >
            <div className="md:flex md:items-center md:justify-between md:gap-6">
              <ul className="space-y-4 md:flex md:space-x-8 md:space-y-0">
                {navItems.map((item) => (
                  <li key={item.id} className="relative group">
                    <button
                      onClick={() => {
                        if (item.id === "about" || item.id === "announcements") {
                          setCurrentPage(item.id);
                          setIsMenuOpen(false);
                          scrollToSection(item.id);
                          return;
                        }
                        setCurrentPage(item.id);
                        setIsMenuOpen(false);
                      }}
                      className={`relative text-lg font-bold transition-colors duration-300 ${currentPage === item.id ? "text-[#0f9e68] md:text-white" : "text-gray-700 hover:text-[#0f9e68] md:text-white md:hover:text-white"
                        }`}
                    >
                      {item.label}
                      <span
                        className={`absolute left-0 -bottom-1 h-[2px] w-full bg-[#0f9e68] md:bg-white transition-transform duration-300 origin-left ${currentPage === item.id ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                          }`}
                      />
                    </button>
                  </li>
                ))}
              </ul>

              <a
                href="https://chat.whatsapp.com/LWRkkGcjK4P7FtVt7JOl7y?s=sh&p=i&ilr=4"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Join our WhatsApp community"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 py-2.5 font-bold text-white transition-colors hover:bg-[#1faf54] md:mt-0 md:w-auto md:shrink-0"
              >
                <FaWhatsapp aria-hidden="true" className="text-xl" />
                Join Our Whatsapp Group
              </a>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

const Footer = () => {
  const socialLinks = [
    { label: "YouTube", href: "https://www.youtube.com/", Icon: FaYoutube },
    { label: "Facebook", href: "https://www.facebook.com/", Icon: FaFacebookF },
    { label: "Instagram", href: "https://www.instagram.com/", Icon: FaInstagram },
    { label: "Twitter", href: "https://twitter.com/", Icon: FaTwitter },
  ];

  return (
    <footer className="mt-16 bg-[#005333] text-white">
      <div className="w-full px-6 py-10 md:px-12">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:items-start">
          <div>
            <h2 className="mb-5 text-2xl font-bold">Contact Us</h2>
            <address className="space-y-2 text-base not-italic leading-relaxed md:text-lg">
              <p>2480 Kingston Road, Scarborough, ON, M1L 1V3, Canada</p>
              <p>
                Tel: <a href="tel:+16478214813" className="hover:underline">+1 (647) 821-4813</a>
              </p>
              <p>
                Email: <a href="mailto:nouh.icc@gmail.com" className="hover:underline">nouh.icc@gmail.com</a>
              </p>
            </address>
          </div>

          <div className="md:justify-self-end">
            <h2 className="mb-5 text-2xl font-bold">Connect with us</h2>
            <div className="flex items-center gap-4">
              {socialLinks.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-xl text-[#005333] transition-colors hover:bg-[#d3ebd6]"
                >
                  <Icon aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
const NOUHWebsite = () => {
  const [currentPage, setCurrentPage] = useState("home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // dynamic data shared with pages
  const [prayerTimes, setPrayerTimes] = useState({
    // bootstrap defaults; replaced by API once it loads
    // iqamatimes: {
    //   fajr: "06:30 AM",
    //   dhuhr: "02:00 PM",
    //   asr: "05:45 PM",
    //   maghrib: "Sunset",
    //   isha: "09:00 PM",
    //   jumuah1_khutba: "1:40 PM",
    //   jumuah1_iqamah: "2:20 PM",
    // },
    // startTimes: {
    //   timings: {
    //     Fajr: "05:40 AM",
    //     Sunrise: "07:16 AM",
    //     Dhuhr: "01:09 PM",
    //     Asr: "04:26 PM",
    //     Maghrib: "Sunset",
    //     Isha: "08:37 PM",
    //   },
    // },
  });
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [services, setServices] = useState([]); // <— no static default
  const [hijriDate, setHijriDate] = useState({ formatted: "" });
  const [dataLoading, setDataLoading] = useState(true);

  // expose nav for debugging
  useEffect(() => {
    window.currentPage = { setCurrentPage };
  }, [setCurrentPage]);

  // fetch services for Home page sections (API only, no static fallback)
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const svc = await getMasjidServices(MASJID_ID, { signal: ac.signal });
        setServices(Array.isArray(svc) ? svc : []);
      } catch (e) {
        console.warn("Services API failed:", e?.message);
        setServices([]); // leave empty if error
      }
    })();
    return () => ac.abort();
  }, []);

  // fetch combined data (prayers, events, services?, hijri)
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setDataLoading(true);
        const data = await getAllMasjidData(MASJID_ID);

        const data1 = await getPrayerTimes(MASJID_ID);
        if (data1) setPrayerTimes(data1);

        if (Array.isArray(data?.events) && data.events.length) {
          const norm = data.events.map(normalizeEvent).filter(Boolean);
          setEvents(norm);
        }

        setAnnouncements(Array.isArray(data?.announcements) ? data.announcements : []);

        if (data?.hijriDate?.formatted) setHijriDate(data.hijriDate);
      } catch (err) {
        console.error("getAllMasjidData failed:", err);
        // keep whatever services we already have from the services call
      } finally {
        setDataLoading(false);
      }
    };
    fetchAllData();
  }, []);

  // hijri (client side best-effort)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const d = await getHijriDate();
        if (mounted && d) setHijriDate(d);
      } catch { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, []);

  // small splash timeout
  useEffect(() => {
    const t = setTimeout(() => setIsLoading(false), 1200);
    return () => clearTimeout(t);
  }, []);

  const renderPage = () => {
    switch (currentPage) {
      case "home":
        return (
          <HomePage
            setCurrentPage={setCurrentPage}
            prayerTimes={prayerTimes}
            events={events}
            announcements={announcements}
            services={services}
            hijriDate={hijriDate}
            dataLoading={dataLoading}
          />
        );
      case "announcements":
      case "about":
        return (
          <HomePage
            setCurrentPage={setCurrentPage}
            prayerTimes={prayerTimes}
            events={events}
            announcements={announcements}
            services={services}
            hijriDate={hijriDate}
            dataLoading={dataLoading}
          />
        );
      case "services":
        // IMPORTANT: ServicesPage will fetch its own data; no 'services' prop passed
        return <ServicesPage setCurrentPage={setCurrentPage} masjidId={MASJID_ID} />;
      case "events":
        return <EventsPage setCurrentPage={setCurrentPage} masjidId={MASJID_ID} />;
      case "contact":
        return <ContactPage setCurrentPage={setCurrentPage} />;
      default:
        return (
          <HomePage
            setCurrentPage={setCurrentPage}
            prayerTimes={prayerTimes}
            events={events}
            announcements={announcements}
            services={services}
            hijriDate={hijriDate}
            dataLoading={dataLoading}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Preloader isLoading={isLoading || dataLoading} />
      <Header
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      />
      <main className="pb-4">{renderPage()}</main>
      <Footer setCurrentPage={setCurrentPage} />
    </div>
  );
};

export default NOUHWebsite;
